import { Component, ViewChild, effect, signal, untracked, ChangeDetectionStrategy } from '@angular/core';

import { IEntityMetaData } from '@app/common-codes/_models/IEntityMetaData';
import { GenericListComponent } from '@app/common-codes/components/list-components/generic-base-list/generic-list.component';
import { Register } from '@app/common-codes/decorators/DependencyInjectionRegistreyDecorators';
import { ListTableColumnComponent } from '@app/common-codes/components/list-components/list-table-column/list-table-column.component';
import { ListViewComponent } from '@app/common-codes/components/list-components/list-view/list-view.component';
import { MatwinsSkins } from '@app/common-codes/skins/global.skin';
import { AlertModalService } from '@app/common-codes/services/AlertModalService';
import { IStaticStylesConfig } from '@app/common-codes/skins/_primitives/IStaticStylesConfig';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';
import { GenetricDetailComponent } from '@app/common-codes/components/edit-components/generic-detail.component';
import { BuzzerConfig } from '@app/common-codes/models/matwins-tenant/BuzzerConfig.model';
import { LookupProfile, LookupItem } from '@app/common-codes/services/LookupService';
import { BuzzerConfigDetailComponent } from '../buzzer-config-detail/buzzer-config-detail.component';
import { ReactiveBadgeComponent } from '@app/common-codes/components/nested-components/reactive-badge/reactive-badge.component';

@Register(BuzzerConfig)
@Component({
  selector: 'app-buzzer-config-list',
  templateUrl: './buzzer-config-list.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ListViewComponent,
    BuzzerConfigDetailComponent,
    ListTableColumnComponent,
    TranslatePipe,
    ReactiveBadgeComponent
]
})
export class BuzzerConfigListComponent extends GenericListComponent<BuzzerConfig> {

  override Skin = MatwinsSkins.V1 as IStaticStylesConfig;
  override ComponentEntityClass = BuzzerConfig;
  override entityMetaData: IEntityMetaData = new BuzzerConfig().GetType();
  override contextMenuItems = [];
  @ViewChild('buzzerConfigDetail', { read: BuzzerConfigDetailComponent }) buzzerConfigDetail!: BuzzerConfigDetailComponent;

  buzzerStatusLookup = signal<LookupItem[]>([]);
  showFilter = signal<'all' | 'visible'>('visible');

  /// R-15-077 : une sonnette est liee a un POS, il faut donc en avoir selectionne un.
  get canCreateBuzzer(): boolean {
    return !!this.mediator?.selectedLevel1Id();
  }

  /** Sujet affiche en liste, tronque a 15 caracteres. */
  getSubject(entity: BuzzerConfig): string {
    const value = entity?.subjectTranslations?.[0]?.translationValue ?? '';
    return value.length > 15 ? `${value.slice(0, 15)}...` : value;
  }

  public static override get lookupProfiles(): LookupProfile[] {
    return [
      { lookupName: "BuzzerStatus" },
      { lookupName: "Employee" },
    ];
  }

  constructor(_alertModalService: AlertModalService) {
    super();
    this.alertModalService = _alertModalService;

    effect(() => {
      const entities = this.listView()?.entities();
      if (entities) {
        // Lire la revision : si les listes arrivent en retard, l'effet repasse et les remplit.
        this.lookupService.revision();
        this.buzzerStatusLookup.set(this.lookupService.getLookupData('BuzzerStatus'));
      }
    });

    effect(() => {
      const mode = this.showFilter();
      const listView = this.listView();
      if (!listView) return;

      // Non traque : l'effet ecrit lui-meme `entities`, le lire en dependance le ferait boucler.
      const entities = untracked(() => listView.entities()?.items) ?? this.cache;
      if (entities.length > 0) {
        if (this.isNested) {
          this.applyLocalFilterSortPaginate();
        } else {
          this.applyLocalPagination(0, listView.pageSize());
        }
      }
    });
  }

  // Le filtre "visible" est un filtre local : il vit dans filterItems pour etre
  // applique par tous les chemins de pagination (liste imbriquee ou API).
  protected override filterItems(items: BuzzerConfig[]): BuzzerConfig[] {
    if (this.showFilter() === 'visible') {
      items = items.filter(e => e.show !== false);
    }
    return super.filterItems(items);
  }

  override getdetailComponent(): GenetricDetailComponent<BuzzerConfig> | undefined {
    return this.buzzerConfigDetail;
  }
}
